import { UserRole } from '@lmc/ts-common'

export type RolePreviewBullet = {
  variant: 'tick' | 'cross'
  copyKey: string
}

export type RolePreviewSection = {
  heading: 'careOffice' | 'carerApp'
  items: RolePreviewBullet[]
}

export type RolePreviewExtraNote = {
  // i18nKey for the body. Body may contain <bold>...</bold> and <link>...</link>
  // tokens which RoleInfoCard substitutes via <Trans> components.
  copyKey: string
  // Concrete URL the <link> token resolves to.
  linkHref: string
}

export type RolePreviewCopy = {
  sections: RolePreviewSection[]
  extraNote?: RolePreviewExtraNote
  // Use this i18n key for the heading instead of looking up reportingConstants[role].
  roleNameKey?: string
}

const CARER_KEYWORKER_HELP_URL =
  'https://support.logmycare.co.uk/en/articles/8324521-how-do-i-assign-a-carer-to-a-client'

export const ROLE_PREVIEW_COPY: Partial<Record<string, RolePreviewCopy>> = {
  [UserRole.careHomeAdmin]: {
    sections: [
      {
        heading: 'careOffice',
        items: [
          { variant: 'tick', copyKey: 'ROLE_PREVIEW_ADMIN_CO_BULLET1' },
          { variant: 'tick', copyKey: 'ROLE_PREVIEW_ADMIN_CO_BULLET2' }
        ]
      },
      {
        heading: 'carerApp',
        items: [
          { variant: 'tick', copyKey: 'ROLE_PREVIEW_ADMIN_CA_BULLET1' },
          { variant: 'tick', copyKey: 'ROLE_PREVIEW_ADMIN_CA_BULLET2' }
        ]
      }
    ]
  },
  [UserRole.management]: {
    sections: [
      {
        heading: 'careOffice',
        items: [
          { variant: 'tick', copyKey: 'ROLE_PREVIEW_MGMT_CO_BULLET1' },
          { variant: 'tick', copyKey: 'ROLE_PREVIEW_MGMT_CO_BULLET2' },
          { variant: 'cross', copyKey: 'ROLE_PREVIEW_MGMT_CO_BULLET3' },
          { variant: 'cross', copyKey: 'ROLE_PREVIEW_MGMT_CO_BULLET4' }
        ]
      },
      {
        heading: 'carerApp',
        items: [
          { variant: 'tick', copyKey: 'ROLE_PREVIEW_MGMT_CA_BULLET1' },
          { variant: 'tick', copyKey: 'ROLE_PREVIEW_MGMT_CA_BULLET2' }
        ]
      }
    ]
  },
  [UserRole.seniorCarer]: {
    sections: [
      {
        heading: 'careOffice',
        items: [
          { variant: 'tick', copyKey: 'ROLE_PREVIEW_SC_CO_BULLET1' },
          { variant: 'tick', copyKey: 'ROLE_PREVIEW_SC_CO_BULLET2' },
          { variant: 'cross', copyKey: 'ROLE_PREVIEW_SC_CO_BULLET3' },
          { variant: 'cross', copyKey: 'ROLE_PREVIEW_SC_CO_BULLET4' },
          { variant: 'cross', copyKey: 'ROLE_PREVIEW_SC_CO_BULLET5' },
          { variant: 'cross', copyKey: 'ROLE_PREVIEW_SC_CO_BULLET6' }
        ]
      },
      {
        heading: 'carerApp',
        items: [
          { variant: 'tick', copyKey: 'ROLE_PREVIEW_SC_CA_BULLET1' },
          { variant: 'tick', copyKey: 'ROLE_PREVIEW_SC_CA_BULLET2' }
        ]
      }
    ]
  },
  [UserRole.carer]: {
    sections: [
      {
        heading: 'careOffice',
        items: [{ variant: 'cross', copyKey: 'ROLE_PREVIEW_CARER_CO_BULLET1' }]
      },
      {
        heading: 'carerApp',
        items: [
          { variant: 'tick', copyKey: 'ROLE_PREVIEW_CARER_CA_BULLET1' },
          { variant: 'cross', copyKey: 'ROLE_PREVIEW_CARER_CA_BULLET2' }
        ]
      }
    ],
    extraNote: {
      copyKey: 'ROLE_PREVIEW_CARER_KEYWORKER_NOTE',
      linkHref: CARER_KEYWORKER_HELP_URL
    }
  },
  [UserRole.auditView]: {
    sections: [
      {
        heading: 'careOffice',
        items: [
          { variant: 'tick', copyKey: 'ROLE_PREVIEW_AV_CO_BULLET1' },
          { variant: 'tick', copyKey: 'ROLE_PREVIEW_AV_CO_BULLET2' },
          { variant: 'cross', copyKey: 'ROLE_PREVIEW_AV_CO_BULLET3' }
        ]
      },
      {
        heading: 'carerApp',
        items: [
          { variant: 'tick', copyKey: 'ROLE_PREVIEW_AV_CA_BULLET1' },
          { variant: 'tick', copyKey: 'ROLE_PREVIEW_AV_CA_BULLET2' }
        ]
      }
    ]
  },
  [UserRole.it]: {
    roleNameKey: 'ROLE_LABEL_IT_SECURITY',
    sections: [
      {
        heading: 'careOffice',
        items: [
          { variant: 'tick', copyKey: 'ROLE_PREVIEW_ITS_CO_BULLET1' },
          { variant: 'tick', copyKey: 'ROLE_PREVIEW_ITS_CO_BULLET2' }
        ]
      },
      {
        heading: 'carerApp',
        items: [{ variant: 'cross', copyKey: 'ROLE_PREVIEW_ITS_CA_BULLET1' }]
      }
    ]
  },
  [UserRole.groupAdmin]: {
    sections: [
      {
        heading: 'careOffice',
        items: [
          { variant: 'tick', copyKey: 'ROLE_PREVIEW_GA_CO_BULLET1' },
          { variant: 'tick', copyKey: 'ROLE_PREVIEW_GA_CO_BULLET2' },
          { variant: 'tick', copyKey: 'ROLE_PREVIEW_GA_CO_BULLET3' }
        ]
      }
    ]
  },
  [UserRole.groupManager]: {
    sections: [
      {
        heading: 'careOffice',
        items: [
          { variant: 'tick', copyKey: 'ROLE_PREVIEW_GM_CO_BULLET1' },
          { variant: 'tick', copyKey: 'ROLE_PREVIEW_GM_CO_BULLET2' },
          { variant: 'tick', copyKey: 'ROLE_PREVIEW_GM_CO_BULLET3' },
          { variant: 'cross', copyKey: 'ROLE_PREVIEW_GM_CO_BULLET4' },
          { variant: 'cross', copyKey: 'ROLE_PREVIEW_GM_CO_BULLET5' }
        ]
      }
    ]
  }
}
